import { useState, useMemo } from 'react'
import { DollarSign, Clock, Users } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { useAllBillingStats, useAllPayments } from '../../hooks/api/useBilling'
import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { DateFilterPopover } from '../../components/ui/DateFilterPopover'
import { FilterDropdown, FilterState } from '../../components/ui/FilterDropdown'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

// Payment status options for billing
const PAYMENT_STATUS_OPTIONS = [
  { value: null, label: 'All Statuses' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' }
]

// Helper to format currency
const formatCurrency = (amount: number) => {
  return `$${(amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// Helper to format date
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const BillingDashboardPage = () => {
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const { data: stats, isLoading: statsLoading } = useAllBillingStats()
  const { data: payments, isLoading: paymentsLoading } = useAllPayments()
  const [dateFilter, setDateFilter] = useState('7d')
  const [filters, setFilters] = useState<FilterState>({
    organization: null,
    status: null,
    priority: null
  })

  // Helper to filter by date range
  const getDateCutoff = (filter: string) => {
    const now = new Date()
    switch (filter) {
      case '24h':
        return new Date(now.getTime() - 24 * 60 * 60 * 1000)
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      case '1m':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      case '1y':
        return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)
      default:
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    }
  }

  // Get unique organizations from payments
  const organizations = useMemo(() => {
    if (!payments) return []
    const unique = new Set(payments.map((p: any) => p.companies?.name).filter(Boolean))
    return Array.from(unique) as string[]
  }, [payments])

  // Filter payments based on date filter and organization/status
  const filteredPayments = useMemo(() => {
    if (!payments) return []

    const cutoff = getDateCutoff(dateFilter)
    return payments.filter((p: any) => {
      // Date filter
      const paymentDate = new Date(p.created_at)
      if (paymentDate < cutoff) return false

      // Organization filter
      if (filters.organization && p.companies?.name !== filters.organization) return false

      // Status filter (treating payment status as status)
      if (filters.status && p.status !== filters.status) return false

      return true
    })
  }, [payments, dateFilter, filters])

  // Generate revenue trend data from filtered payments
  const revenueData = useMemo(() => {
    return filteredPayments
      .filter((p: any) => p.status === 'succeeded')
      .reduce((acc: any[], payment: any) => {
        const date = new Date(payment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        const existing = acc.find(item => item.date === date)
        if (existing) {
          existing.revenue += payment.amount / 100
        } else {
          acc.push({ date, revenue: payment.amount / 100 })
        }
        return acc
      }, [])
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }, [filteredPayments])

  if (isLoading || statsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">User not found in database!</p>
            <p className="text-red-500 text-xs">Please contact support.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">Welcome back! Here's your IT health overview.</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Filter Dropdown */}
            <FilterDropdown
              filters={filters}
              onFilterChange={setFilters}
              organizations={organizations}
              statusOptions={PAYMENT_STATUS_OPTIONS}
              showPriority={false}
              storageKey="billing-dashboard-filters"
            />

            {/* Date Filter */}
            <DateFilterPopover
              selectedValue={dateFilter}
              onValueChange={setDateFilter}
            />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Revenue Processed */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center text-gray-600 text-sm mb-2">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Total Revenue Processed
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {stats ? formatCurrency(stats.total_revenue) : '$0.00'}
                </div>
                <div className="text-sm text-gray-600">
                  {stats?.successful_payments || 0} successful payments
                </div>
              </div>
            </div>
          </div>

          {/* Overdue Payments */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center text-gray-600 text-sm mb-2">
                  <Clock className="w-4 h-4 mr-2" />
                  Overdue Payments
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {stats ? formatCurrency(stats.overdue_amount) : '$0.00'}
                </div>
                <div className="text-sm text-red-600">
                  {stats?.failed_payments || 0} failed payments
                </div>
              </div>
            </div>
          </div>

          {/* Active Subscriptions */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center text-gray-600 text-sm mb-2">
                  <Users className="w-4 h-4 mr-2" />
                  Active Subscriptions
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {stats?.active_subscriptions || 0}
                </div>
                <div className="text-sm text-gray-600">
                  {stats?.canceled_subscriptions || 0} canceled
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Trend Chart */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Revenue Trend</h2>
            <p className="text-sm text-gray-600">Track revenue performance over time</p>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(value) => `$${value / 1000}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  formatter={(value: number) => [`$${value.toLocaleString()}`, 'Revenue']}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#000"
                  strokeWidth={2}
                  dot={{ fill: '#000', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payments Section */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Payments</h2>
            <div className="flex items-center space-x-2">
              <button className="px-4 py-2 bg-black text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors">
                Export
              </button>
              <button className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-50 transition-colors">
                Sort
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paymentsLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      Loading payments...
                    </td>
                  </tr>
                ) : !filteredPayments || filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      No payments found
                    </td>
                  </tr>
                ) : (
                  filteredPayments.slice(0, 10).map((payment: any) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {payment.stripe_payment_intent_id.slice(-8).toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.companies?.name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.tier || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          payment.status === 'succeeded'
                            ? 'bg-green-100 text-green-800'
                            : payment.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(payment.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <div>Showing {Math.min(10, filteredPayments.length)} of {filteredPayments.length} payments</div>
            <div className="flex space-x-2">
              <button className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50" disabled={true}>Previous</button>
              <button className="px-3 py-1 text-gray-400 cursor-not-allowed" disabled={true}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
